import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Parameter definitions
const parameterDefinitions = [
  { key: "moduleSerialNumber", label: "Module Serial Number" },
  { key: "goldenModuleReport", label: "Golden Module Report" },
  { key: "alphaValue", label: "Alpha Value" },
  { key: "betaValue", label: "Beta Value" },
  { key: "gammaValue", label: "Gamma Value" },
  { key: "measuredPmax", label: "Measured: Pmax (W)" },
  { key: "measuredISC", label: "Measured: Isc (A)" },
  { key: "measuredVOC", label: "Measured: Voc (V)" },
  { key: "measuredIMP", label: "Measured: Imp (A)" },
  { key: "measuredVMP", label: "Measured: Vmp (V)" },
  { key: "measuredFF", label: "Measured: FF (%)" },
  { key: "nominalPmax", label: "Nominal: Pmax (W)" },
  { key: "nominalISC", label: "Nominal: Isc (A)" },
  { key: "nominalVOC", label: "Nominal: Voc (V)" },
  { key: "nominalIMP", label: "Nominal: Imp (A)" },
  { key: "nominalVMP", label: "Nominal: Vmp (V)" },
  { key: "nominalFF", label: "Nominal: FF (%)" },
];

const PrimaryModuleCalibrationTable = ({ data = [], startDate, endDate, shifts = [], subForm }) => {
  // Get all dates strictly in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to data, or default
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["Morning", "Afternoon", "Night"];
  }, [shifts, data]);

  // Collect all unique lines across the dataset (with fallback)
  const allLines = useMemo(() => {
    const lines = new Set();
    data.forEach((item) => {
      Object.keys(item.form_data?.lines || {}).forEach((line) => {
        if (line) lines.add(line);
      });
    });
    const arr = [...lines].sort();
    return arr.length > 0 ? arr : ["Line 1"];
  }, [data]);

  // Leaf columns: strictly uniform across all dates, shifts, and lines
  const flatColumns = useMemo(() => {
    const list = [];
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        allLines.forEach((line) => {
          list.push({
            date,
            shift,
            line,
            id: `${date}_${shift}_${line}`,
            label: `${shift} (${line})`,
          });
        });
      });
    });
    return list;
  }, [dates, effectiveShifts, allLines]);

  // Column width calculations
  const columnWidths = useMemo(() => {
    const widths = {
      parameter: 230,
      value: 120,
    };

    parameterDefinitions.forEach((param) => {
      widths.parameter = Math.max(widths.parameter, estimateTextWidth(param.label));
    });

    return widths;
  }, []);

  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Static parameter rows
  const tableRows = useMemo(() => {
    return parameterDefinitions.map((param) => ({
      key: param.key,
      parameter: param.label,
    }));
  }, []);

  // Lookup map: `${date}_${shift}_${line}_${paramKey}`
  const tableDataMap = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) return;

      Object.entries(item.form_data?.lines || {}).forEach(([line, lineData]) => {
        if (!lineData || typeof lineData !== "object") return;
        const keyPrefix = `${date}_${shift}_${line}`;

        parameterDefinitions.forEach((param) => {
          const val = lineData[param.key];
          if (val !== undefined && val !== null && val !== "") {
            map[`${keyPrefix}_${param.key}`] = String(val);
          }
        });
      });
    });
    return map;
  }, [data]);

  const linesPerShift = allLines.length;
  const colsPerDate = effectiveShifts.length * linesPerShift;

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    const row1 = ["Parameter"];
    const row2 = [""];

    merges.push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } });

    let colIdx = 1;

    dates.forEach((date) => {
      row1.push(date);
      for (let i = 1; i < colsPerDate; i++) row1.push("");

      if (colsPerDate > 1) {
        merges.push({
          s: { r: 0, c: colIdx },
          e: { r: 0, c: colIdx + colsPerDate - 1 },
        });
      }

      effectiveShifts.forEach((shift) => {
        allLines.forEach((line) => {
          row2.push(`Shift ${shift} (${line})`);
          colIdx += 1;
        });
      });
    });

    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      flatColumns.forEach((col) => {
        const val = tableDataMap[`${col.id}_${row.key}`];
        r.push(val !== undefined ? val : "N/A");
      });
      return r;
    });

    const sheetData = [row1, row2, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    if (merges.length > 0) ws["!merges"] = merges;

    ws["!cols"] = [
      { wch: 30 },
      ...Array(flatColumns.length).fill({ wch: 18 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Primary Module Cal");

    const fileName = generateExcelFileName("Primary_Module_Calibration", startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Primary (Golden) Module Calibration Record
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dates.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Parameter + Dates (merged across shifts and lines) */}
            <TableRow>
              <StickyTableCell
                rowSpan={2}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {dates.map((date) => (
                <CustomTableCell
                  key={date}
                  colSpan={colsPerDate}
                  columnWidth={columnWidths.value * colsPerDate}
                >
                  {date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Shift (Line) Sub-headers */}
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.flatMap((shift) =>
                  allLines.map((line) => (
                    <CustomTableCell
                      key={`${date}_${shift}_${line}`}
                      columnWidth={columnWidths.value}
                    >
                      {shift} ({line})
                    </CustomTableCell>
                  ))
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={1}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No dates selected
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>

                  {flatColumns.map((col) => {
                    const val = tableDataMap[`${col.id}_${row.key}`];
                    return (
                      <BodyTableCell key={`${col.id}_${row.key}`} columnWidth={columnWidths.value}>
                        {val !== undefined ? val : "N/A"}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default PrimaryModuleCalibrationTable;